import { h, render, Fragment } from 'preact';
import { useRef, useEffect, useState, useMemo } from 'preact/hooks';
import clsx from 'clsx';
import IconCart from '../utils/icon/cart.svg';
import IconHamburgerClosed from '../utils/icon/hamburger-closed.svg';
import SearchForm from './search-form';
import MainMenu from './main-menu';
import Image from '../utils/image';
import { useWindowSize } from '../utils/window-size';
import PromoBar from './promo-bar';
import MobileMenu from './mobile-menu';
import Backdrop from '../utils/backdrop';
import StickySubHeader from './sticky-sub-header';


const Header = ({ data }) => {
  const { width } = useWindowSize();
  const promoBarRef = useRef(null);
  const mainNavbarRef = useRef(null);
  const HEADER_HEIGHT = 120;
  const headerContainer = document.querySelector('.site-header');
  const mobileOpenTrigger = useRef(null);
  const [showBackDrop, setShowBackDrop] = useState(false);
  const [isVoyagesLayout, setHeaderVoyagesLayout] = useState(false);
  const cartOpenEvent = new CustomEvent('cart:open');
  const [menuTriggers, setMenuTriggers] = useState({
    openTriggerEl: null,
    closeTriggerEl: null,
  });
  const {
    is_transparent: isTransparent,
    logo,
    logo_light: logoLight,
    logo_for_transparent_header: logoForTransparentHeader,
    enableVNav: showSubHeader,
    vShowShopAllCta
  } = data;


  const isTransparencyEnabled = isTransparent === 'true';
  const [stickThreshold, setStickThreshold] = useState(0);
  const [isHeaderTransparent, setIsHeaderTransparent] = useState(
    isTransparencyEnabled,
  );
  const headerDom = document.querySelector('[data-section="header"]');
  const headerHeight = headerDom?.offsetHeight || HEADER_HEIGHT;

  const scrollHandler = () => {
    if (window.pageYOffset >= (stickThreshold || headerHeight)) {
      setIsHeaderTransparent(false);
    } else {
      setIsHeaderTransparent(true);
    }
  };

  let animationFrameId = null;
  const throttledScrollHandler = () => {
    if (animationFrameId) {
      cancelAnimationFrame(animationFrameId);
    }

    animationFrameId = requestAnimationFrame(scrollHandler);
  };

  if (isTransparencyEnabled) {
  useEffect(() => {
      scrollHandler();
      window.addEventListener('scroll', throttledScrollHandler);

      return () => {
        window.removeEventListener('scroll', throttledScrollHandler);
        cancelAnimationFrame(animationFrameId);
      };
    }, []);
  }

  useEffect(() => {
    try {
      let topAdjustment = promoBarRef?.current?.clientHeight;

      if (showSubHeader) {
        topAdjustment += mainNavbarRef?.current?.clientHeight || 0;
        setStickThreshold(topAdjustment);
      }
      headerContainer.style.top = `-${topAdjustment}px`;
      headerContainer.style.zIndex = '20';
    } catch (e) {
      // no header container on page yet
    }
  }, [
    width,
    promoBarRef?.current?.clientHeight,
    mainNavbarRef?.current?.clientHeight,
    showSubHeader
  ]);

  useEffect(() => {
    setMenuTriggers({
      openTriggerEl: mobileOpenTrigger.current,
    });
  }, [mobileOpenTrigger]);

  const promoBarMemo = useMemo(
    () => <PromoBar data={data} promoBarRef={promoBarRef} />,
    [data],
  );

  const layoutClasses = () =>
    isHeaderTransparent
      ? 'bg-transparent border-b border-white-50 text-white [&_[data-cart-count]]:text-white'
      : 'bg-neutral laptop:border-b border-black-30';

  const logoImage = () =>
    isHeaderTransparent ? (
      <Image src={logoForTransparentHeader} />
    ) : (
      <Image src={logo} />
    );

  return (
    <>
      <div className="z-[41] relative">
        {promoBarMemo}
        <div
          ref={mainNavbarRef}
          className={clsx(
            'w-full laptop:border-b pt-4 pb-[15px] laptop:py-0',
            isTransparencyEnabled ? 'absolute' : 'relative',
            layoutClasses(),
            isVoyagesLayout
              ? 'bg-[#3A3A3C] border-white-50 text-neutral transition-[background-color] duration-300 ease-in-out'
              : 'bg-neutral border-black-30 transition-[background-color] duration-300 ease-in-out',
          )}
        >
          <div className="flex justify-between container laptop:px-10 flex-wrap laptop:flex-nowrap">
            <a
              href={window.Shopify.routes.root}
              className="link-unstyled min-w-[86px] logo w-[86px] relative ml-5 laptop:ml-0 flex items-center"
            >
              {isVoyagesLayout ? (
                <Image src={logoLight} />
              ) :
                logoImage()
              }
            </a>
            <div className="hidden laptop:block mr-auto w-full laptop:pt-0 pt-[26px]">
              <MainMenu
                menu={data.menus[0].desktop_menu}
                className="pt-[26px]"
                setShowBackDrop={setShowBackDrop}
                setHeaderVoyagesLayout={setHeaderVoyagesLayout}
                vShowShopAllCta={vShowShopAllCta}
              />
            </div>
            <div className={'flex items-center pr-5 laptop:pr-0'}>
              <div className={'mr-4 laptop:mr-[27px] flex items-center'}>
                <SearchForm
                  search_form={data.search_form}
                  darkLayout={isHeaderTransparent || isVoyagesLayout} />
              </div>
              <button
                type="button"
                className="relative h-[24px] w-[20px]"
                onClick={() => document.dispatchEvent(cartOpenEvent)}
              >
                <IconCart />
                <span
                  className={clsx(
                    'absolute top-[9.5px] laptop:top-[9px] font-base text-[10px] w-full inset-x-0 text-center',
                    (isHeaderTransparent || isVoyagesLayout) ? 'text-white' : 'text-[#242424]',
                  )}
                  data-cart-count
                ></span>
              </button>
              <button className="ml-4 laptop:hidden" ref={mobileOpenTrigger}>
                <IconHamburgerClosed />
              </button>
            </div>
          </div>
        </div>
      </div>
      <Backdrop isOpen={showBackDrop} setIsOpen={setShowBackDrop} />
      <MobileMenu
        logo={data.logo}
        menu={data.menus[0].desktop_menu}
        menuTriggers={menuTriggers}
        customer={data.customer}
        locatorUrl={data.locatorUrl}
        vShowShopAllCta={vShowShopAllCta}
      />
      {showSubHeader && (
        <StickySubHeader
          stickThreshold={stickThreshold}
          logo={data.vNavLogo}
          logoLink={data.vNavLogoLink}
          menu={data.vNavMenu}
          ctaText={data.vNavCtaText}
          ctaUrl={data.vNavCtaUrl}
        />
      )}
    </>
  );
};

export default (element, data) => {
  const init = () => {
    render(<Header className="header-component" data={data} />, element);
  };

  init();

};
